import { RowSelectionState } from "@tanstack/react-table";
import { NestedEditorRow } from "./nestedRowTypes";

// Selection includes variables; bulk actions still exclude them.
export const isNestedRowSelectable = (row: NestedEditorRow): boolean =>
    row.kind === "account" || row.kind === "group";

// The ids of every selectable row in the tree (depth-first), used to size the
// footer's "Select all" and to count the current selection. Group and account
// ids are unique across the tree, so a flat id list is sufficient.
export const collectSelectableNestedRowIds = (
    rows: NestedEditorRow[],
): string[] => {
    const ids: string[] = [];
    const visit = (row: NestedEditorRow): void => {
        if (isNestedRowSelectable(row)) {
            ids.push(row.id);
        }
        row.children?.forEach(visit);
    };
    rows.forEach(visit);
    return ids;
};

// The ids of every custom group row in the tree (depth-first). Used to split a
// selection into its group subset so bulk Delete can remove selected groups (a
// group-only selection has no account bulk action otherwise).
export const collectNestedGroupRowIds = (rows: NestedEditorRow[]): string[] => {
    const ids: string[] = [];
    const visit = (row: NestedEditorRow): void => {
        if (row.kind === "group") {
            ids.push(row.id);
        }
        row.children?.forEach(visit);
    };
    rows.forEach(visit);
    return ids;
};

// The currently-selected draggable rows (accounts + custom groups) in tree
// order (depth-first). Drives multi-row drag-and-drop: the relative order here
// is preserved when the set is moved as a block. Rows no longer present are
// naturally excluded since we only walk the live tree.
export const collectSelectedDraggableNestedRows = ({
    rows,
    rowSelection,
}: {
    rows: NestedEditorRow[];
    rowSelection: RowSelectionState;
}): NestedEditorRow[] => {
    const selectedRows: NestedEditorRow[] = [];
    const visit = (row: NestedEditorRow): void => {
        if (isNestedRowSelectable(row) && rowSelection[row.id]) {
            selectedRows.push(row);
        }
        row.children?.forEach(visit);
    };
    rows.forEach(visit);
    return selectedRows;
};

export const buildNestedSelectAllRowSelection = (
    selectableRowIds: string[],
): RowSelectionState =>
    Object.fromEntries(selectableRowIds.map((id) => [id, true] as const));

// Count of currently-selected selectable rows (accounts + groups), driving the
// footer's "N selected" total. Selections of rows no longer present (e.g. a
// deleted row) are ignored by intersecting against the live selectable ids.
export const countSelectedNestedRows = ({
    selectableRowIds,
    rowSelection,
}: {
    selectableRowIds: string[];
    rowSelection: RowSelectionState;
}): number => selectableRowIds.filter((id) => rowSelection[id]).length;
